<template>
  <div>
    <div class="loading" v-if="fetchData">
      <load-data></load-data>
    </div>

    <div v-else-if="step === 'form'">

      <!-- Group Name -->
      <b-form-group label="Group Name" label-class="font-weight-bold required-label">
        <b-form-input
          v-model="form.name"
          placeholder="Enter group name"
          :state="nameState"
          trim
        />
        <b-form-invalid-feedback>Group name is required.</b-form-invalid-feedback>
      </b-form-group>

      <!-- Grouping Level -->
      <b-form-group label="Course creation Level" label-class="font-weight-bold required-label">
        <b-form-select
          v-model="form.grouping_level"
          :options="levelOptions"
          :state="levelState"
          @change="onLevelChange"
        />
        <b-form-invalid-feedback>Please select a course creation level.</b-form-invalid-feedback>
      </b-form-group>

      <!-- PROGRAM flow -->
      <template v-if="form.grouping_level === 'PROGRAM'">

        <b-form-group label="Program" label-class="font-weight-bold required-label">
          <div v-if="loadingPrograms" class="text-muted small py-1">Loading...</div>
          <div v-else-if="programOptions.length" class="multi-select-box">
            <b-form-checkbox-group
              v-model="form.program_ids"
              :options="programOptions"
              stacked
              @change="onProgramChange"
            />
          </div>
          <p v-else class="text-muted small mb-0">No programs found.</p>
          <small v-if="validated && !form.program_ids.length" class="text-danger">Please select a program.</small>
        </b-form-group>

        <b-form-group
          label="Regulation/Scheme"
          label-class="font-weight-bold required-label"
          v-if="form.program_ids.length"
        >
          <b-form-select
            v-model="form.scheme"
            :options="schemeOptions"
            :state="schemeState"
            :disabled="loadingSubgroups"
            @change="onSchemeChange"
          />
          <b-form-invalid-feedback>Please select a scheme.</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="Class ID"
          label-class="font-weight-bold required-label"
          v-if="form.scheme"
        >
          <b-form-select
            v-model="form.semester"
            :options="semesterOptions"
            @change="onSemesterChange"
          />
        </b-form-group>

        <b-form-group
          label="Classes"
          label-class="font-weight-bold"
          v-if="form.semester"
        >
          <div v-if="filteredSubgroups.length" class="multi-select-box">
            <b-form-checkbox-group
              v-model="form.usersubgroup_ids"
              :options="filteredSubgroupOptions"
              stacked
            />
          </div>
          <p v-else class="text-muted small mb-0">No classes found for the selected semester.</p>
        </b-form-group>

        <b-form-group v-if="form.semester" class="mb-2">
          <b-form-checkbox v-model="form.include_idc" @change="onIdcToggle">
            Include IDC
          </b-form-checkbox>
        </b-form-group>

        <b-form-group label="IDC Classes" label-class="font-weight-bold" v-if="form.include_idc && form.semester">
          <div v-if="loadingIdcSubgroups" class="text-muted small py-1">Loading...</div>
          <div v-else-if="idc_subgroups.length" class="multi-select-box">
            <b-form-checkbox-group v-model="form.idc_usersubgroup_ids" :options="idcSubgroupOptions" stacked />
          </div>
          <p v-else class="text-muted small mb-0">No IDC classes found.</p>
        </b-form-group>

        <b-form-group
          label="Course Type"
          label-class="font-weight-bold"
          v-if="form.scheme"
        >
          <div v-if="courseTypeOptions.length" class="multi-select-box">
            <b-form-checkbox-group
              v-model="form.course_type_ids"
              :options="courseTypeOptions"
              stacked
            />
          </div>
          <p v-else class="text-muted small mb-0">No course types found.</p>
        </b-form-group>

      </template>

      <!-- INSTITUTION flow -->
      <template v-if="form.grouping_level === 'INSTITUTION'">

        <b-form-group label="Regulation/Scheme" label-class="font-weight-bold">
          <b-form-select
            v-model="form.scheme"
            :options="['2012','2013','2014','2015','2016','2017','2018','2019','2020','2021','2022','2023','2024','2025','2026']"
            @change="onInstitutionSchemeChange"
          />
        </b-form-group>

        <b-form-group label="Class ID" label-class="font-weight-bold" v-if="form.scheme">
          <b-form-select
            v-model="form.semester"
            :options="semesterOptions"
            @change="onInstitutionSemesterChange"
          />
        </b-form-group>

        <b-form-group label="Classes" label-class="font-weight-bold" v-if="form.semester">
          <div v-if="loadingSubgroups" class="text-muted small py-1">Loading...</div>
          <div v-else-if="filteredSubgroups.length" class="multi-select-box">
            <b-form-checkbox-group
              v-model="form.usersubgroup_ids"
              :options="filteredSubgroupOptions"
              stacked
            />
          </div>
          <p v-else class="text-muted small mb-0">No classes found for the selected criteria.</p>
        </b-form-group>

        <b-form-group v-if="form.semester" class="mb-2">
          <b-form-checkbox v-model="form.include_idc" @change="onIdcToggle">
            Include IDC
          </b-form-checkbox>
        </b-form-group>

        <b-form-group label="IDC Classes" label-class="font-weight-bold" v-if="form.include_idc && form.semester">
          <div v-if="loadingIdcSubgroups" class="text-muted small py-1">Loading...</div>
          <div v-else-if="idc_subgroups.length" class="multi-select-box">
            <b-form-checkbox-group v-model="form.idc_usersubgroup_ids" :options="idcSubgroupOptions" stacked />
          </div>
          <p v-else class="text-muted small mb-0">No IDC classes found.</p>
        </b-form-group>

        <b-form-group label="Course Type" label-class="font-weight-bold" v-if="form.scheme">
          <div v-if="courseTypeOptions.length" class="multi-select-box">
            <b-form-checkbox-group
              v-model="form.course_type_ids"
              :options="courseTypeOptions"
              stacked
            />
          </div>
          <p v-else class="text-muted small mb-0">No course types found.</p>
        </b-form-group>

      </template>

      <!-- Actions -->
      <div class="d-flex justify-content-end mt-3" style="gap: 8px;">
        <b-button variant="secondary" size="sm" @click="$emit('cancel')" :disabled="previewing">
          Cancel
        </b-button>
        <b-button variant="success" size="sm" @click="initiateGrouping" :disabled="previewing">
          <b-spinner small v-if="previewing" class="mr-1" />
          Initiate course creation
        </b-button>
      </div>

    </div>

    <!-- Preview step -->
    <div v-else-if="step === 'preview'">

      <div class="d-flex align-items-center mb-3">
        <b-button variant="outline-secondary" size="sm" class="mr-2" @click="step = 'form'" :disabled="submitting">
          <i class="fas fa-arrow-left mr-1" style="font-size: 14px !important; vertical-align: middle;"></i><span style="vertical-align: middle;">Back</span>
        </b-button>
        <h6 class="mb-0 font-weight-bold">
          Preview: <span class="text-primary">{{ form.name }}</span>
          <span class="text-muted ml-2">{{ uniqueCodeCount }} course group<span v-if="uniqueCodeCount !== 1">s</span></span>
          <span class="text-muted ml-1">· {{ previewCourses.length }} offering<span v-if="previewCourses.length !== 1">s</span></span>
        </h6>
      </div>

      <div v-if="!previewCourses.length" class="text-muted small py-2">
        No courses matched the selected filters.
      </div>

      <div v-else class="preview-table-wrap">
        <table class="table table-sm table-bordered mb-0">
          <thead class="thead-light">
            <tr>
              <th style="width:36px" class="text-center">
                <input type="checkbox" v-model="allSelected" />
              </th>
              <th style="width:60px" class="text-center">Sl.No</th>
              <th style="width:130px">Course Code</th>
              <th>Course Offerings</th>
              <th style="width:150px">Program</th>
              <th style="width:110px">Class Code</th>
              <th style="width:110px">Academic Year</th>
            </tr>
          </thead>
          <!-- One tbody per code group so rowspan is reliable -->
          <tbody
            v-for="(group, groupIndex) in previewGroups"
            :key="group.code"
            :class="group.shaded ? 'group-shaded' : ''"
          >
            <!-- First row: code + names cells span all sub-rows -->
            <tr>
              <td :rowspan="group.courses.length" class="text-center align-middle">
                <input
                  type="checkbox"
                  :checked="group.courses.every(c => selectedCourseIds.includes(c.id))"
                  @change="toggleGroup(group, $event.target.checked)"
                />
              </td>
              <td :rowspan="group.courses.length" class="text-center align-middle">
                {{ groupIndex + 1 }}
              </td>
              <td :rowspan="group.courses.length" class="align-middle font-weight-bold">
                {{ group.code }}
              </td>
              <td :rowspan="group.courses.length" class="align-middle">
                <div v-for="(c, ci) in group.courses" :key="ci">
                  {{ c.name }}
                  <hr v-if="ci < group.courses.length - 1" class="my-1" />
                </div>
              </td>
              <td>{{ group.courses[0].program_name }}</td>
              <td>{{ group.courses[0].class_code }}</td>
              <td>{{ group.courses[0].academic_year }}</td>
            </tr>
            <!-- Remaining rows: program + class code + academic year only -->
            <tr v-for="course in group.courses.slice(1)" :key="course.id">
              <td>{{ course.program_name }}</td>
              <td>{{ course.class_code }}</td>
              <td>{{ course.academic_year }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex justify-content-end mt-3" style="gap: 8px;">
        <b-button variant="secondary" size="sm" @click="step = 'form'" :disabled="submitting">
          Back
        </b-button>
        <b-button
          variant="success"
          size="sm"
          @click="showConfirmWarning = true"
          :disabled="submitting || !selectedCourseIds.length"
        >
          Confirm &amp; Generate
        </b-button>
      </div>

    </div>

    <!-- Confirmation warning modal -->
    <b-modal
      v-model="showConfirmWarning"
      title="Confirm Generation"
      centered
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="d-flex align-items-start">
        <i class="fas fa-exclamation-triangle text-warning mr-3 mt-1" style="font-size: 1.4rem;"></i>
        <p class="mb-0">No further changes can be made once confirmed.</p>
      </div>
      <template #modal-footer>
        <b-button variant="secondary" size="sm" :disabled="submitting" @click="showConfirmWarning = false">
          Cancel
        </b-button>
        <b-button variant="success" size="sm" :disabled="submitting" @click="onConfirmWarningAccepted">
          <b-spinner small v-if="submitting" class="mr-1" />
          <span style="vertical-align: middle;">Confirm</span>
        </b-button>
      </template>
    </b-modal>

  </div>
</template>

<script>
export default {
  name: "GenerateCourseModal",

  data() {
    return {
      fetchData: false,
      previewing: false,
      submitting: false,
      showConfirmWarning: false,
      validated: false,
      loadingPrograms: false,
      loadingSubgroups: false,
      loadingIdcSubgroups: false,
      idc_subgroups: [],

      step: 'form',
      previewCourses: [],
      selectedCourseIds: [],

      programs: [],
      programSubgroups: [],
      subtypeOptions: [],

      form: {
        name: "",
        grouping_level: null,
        program_ids: [],
        scheme: null,
        semester: null,
        usersubgroup_ids: [],
        idc_usersubgroup_ids: [],
        include_idc: false,
        course_type_ids: [],
      },

      semesterOptions: [
        { value: null, text: "Select semester", disabled: true },
        ...["S1","S2","S3","S4","S5","S6","S7","S8","S9","S10"].map(s => ({ value: s, text: s })),
      ],

      levelOptions: [
        { value: null,          text: "Select level",  disabled: true },
        { value: "PROGRAM",     text: "Program" },
        { value: "INSTITUTION", text: "Institution" },
      ],

    };
  },

  watch: {
    step(newStep) {
      this.$emit("title-change", newStep === "preview" ? "Add Courses" : "Generate Courses");
    },
  },

  computed: {
    nameState() {
      if (this.form.name.trim().length > 0) return true;
      if (!this.validated) return null;
      return false;
    },
    levelState() {
      if (this.form.grouping_level) return true;
      if (!this.validated) return null;
      return false;
    },
    schemeState() {
      if (this.form.scheme) return true;
      if (!this.validated) return null;
      return false;
    },

    programOptions() {
      return this.programs.map(p => ({ value: p.id, text: p.name }));
    },

    courseTypeOptions() {
      return this.subtypeOptions.map(t => ({ value: t.id, text: t.name }));
    },

    schemeOptions() {
      const schemes = [...new Set(this.programSubgroups.map(s => s.scheme).filter(Boolean))].sort();
      return [
        { value: null, text: "Select scheme", disabled: true },
        ...schemes.map(s => ({ value: s, text: s })),
      ];
    },

    filteredSubgroups() {
      if (!this.form.scheme || !this.form.semester) return [];
      return this.programSubgroups.filter(
        s => s.scheme === this.form.scheme && s.class_id === this.form.semester
      );
    },

    filteredSubgroupOptions() {
      return this.filteredSubgroups.map(s => ({
        value: s.id,
        text: [s.name, s.usergroup?.name, s.academic_year].filter(Boolean).join(' - '),
      }));
    },
    idcSubgroupOptions() {
      return this.idc_subgroups.map(s => ({
        value: s.id,
        text: [s.name, s.usergroup?.name, s.academic_year].filter(Boolean).join(' - '),
      }));
    },

    uniqueCodeCount() {
      return new Set(this.previewCourses.map(c => c.code)).size;
    },

    allSelected: {
      get() {
        return this.previewCourses.length > 0 &&
          this.selectedCourseIds.length === this.previewCourses.length;
      },
      set(val) {
        this.selectedCourseIds = val ? this.previewCourses.map(c => c.id) : [];
      },
    },

    previewGroups() {
      const sorted = [...this.previewCourses].sort((a, b) =>
        (a.code || '').localeCompare(b.code || '')
      );
      const groups = [];
      let shadeToggle = false;
      sorted.forEach(c => {
        const last = groups[groups.length - 1];
        if (last && last.code === c.code) {
          last.courses.push(c);
        } else {
          shadeToggle = !shadeToggle;
          groups.push({ code: c.code, courses: [c], shaded: shadeToggle });
        }
      });
      return groups;
    },

  },

  async created() {
    this.fetchData = true;
    await Promise.all([this.fetchSubtypes(), this.fetchPrograms()]);
    this.fetchData = false;
  },

  methods: {
    async fetchSubtypes() {
      try {
        const res = await this.$axios.get(
          `${this.$store.getters.getAPIKey.mainAPI}/coursetypes`
        );
        this.subtypeOptions = res.data.map(({ id, name }) => ({ id, name }));
      } catch { /* silently fail */ }
    },

    async fetchPrograms() {
      this.loadingPrograms = true;
      try {
        const res = await this.$axios.get(
          `${this.$store.getters.getAPIKey.mainAPI}/usergroups?allusergroups=1`
        );
        this.programs = res.data;
      } catch { /* silently fail */ }
      this.loadingPrograms = false;
    },

    async fetchFilteredCourses() {
      const payload = {};
      if (this.form.program_ids.length)      payload.program_ids      = this.form.program_ids;
      if (this.form.scheme)                  payload.scheme           = this.form.scheme;
      if (this.form.semester) {
        const classIds = [this.form.semester];
        if (this.form.include_idc) classIds.push('IDC');
        payload.class_ids = classIds;
      }
      const allSubgroupIds = [...this.form.usersubgroup_ids, ...this.form.idc_usersubgroup_ids];
      if (allSubgroupIds.length) payload.usersubgroup_ids = allSubgroupIds;
      if ((this.form.course_type_ids || []).length)
        payload.course_type_ids = this.form.course_type_ids;
      if (this.form.grouping_level)          payload.grouping_level   = this.form.grouping_level;

      const res = await this.$axios.post(
        `${this.$store.getters.getAPIKey.mainAPI}/course-groups/filtered`,
        payload
      );
      const raw = res.data.data ?? res.data ?? {};
      // Backend returns data grouped by code — flatten to a plain array
      return Array.isArray(raw) ? raw : Object.values(raw).flat();
    },

    onLevelChange() {
      this.form.program_ids = [];
      this.form.scheme = null;
      this.form.semester = null;
      this.form.usersubgroup_ids = [];
      this.form.course_type_ids = [];
      this.programSubgroups = [];
    },

    async onProgramChange() {
      this.form.scheme = null;
      this.form.semester = null;
      this.form.usersubgroup_ids = [];
      this.form.course_type_ids = [];
      this.programSubgroups = [];
      if (!this.form.program_ids.length) return;
      this.loadingSubgroups = true;
      try {
        const res = await this.$axios.get(
          `${this.$store.getters.getAPIKey.mainAPI}/course-groups/subgroups`,
          { params: { usergroup_ids: this.form.program_ids } }
        );
        this.programSubgroups = res.data;
      } finally {
        this.loadingSubgroups = false;
      }
    },

    onInstitutionSchemeChange() {
      this.form.semester = null;
      this.form.usersubgroup_ids = [];
      this.form.include_idc = false;
      this.form.idc_usersubgroup_ids = [];
      this.idc_subgroups = [];
      this.programSubgroups = [];
    },

    async onInstitutionSemesterChange() {
      this.form.usersubgroup_ids = [];
      this.form.idc_usersubgroup_ids = [];
      this.idc_subgroups = [];
      this.programSubgroups = [];
      if (!this.form.semester) return;
      this.loadingSubgroups = true;
      try {
        const res = await this.$axios.get(
          `${this.$store.getters.getAPIKey.mainAPI}/course-groups/subgroups`,
          { params: { scheme: this.form.scheme, class_id: this.form.semester } }
        );
        this.programSubgroups = res.data;
        this.form.usersubgroup_ids = res.data.map(s => s.id);
      } finally {
        this.loadingSubgroups = false;
      }
      if (this.form.include_idc) await this.fetchIdcSubgroups();
    },

    onSchemeChange() {
      this.form.semester = null;
      this.form.usersubgroup_ids = [];
      this.form.include_idc = false;
      this.form.idc_usersubgroup_ids = [];
      this.idc_subgroups = [];
      this.form.course_type_ids = [];
    },

    async fetchIdcSubgroups() {
      this.idc_subgroups = [];
      this.form.idc_usersubgroup_ids = [];
      this.loadingIdcSubgroups = true;
      try {
        const res = await this.$axios.get(
          `${this.$store.getters.getAPIKey.mainAPI}/course-groups/subgroups`,
          { params: { class_id: 'IDC', scheme: this.form.scheme } }
        );
        this.idc_subgroups = res.data;
        this.form.idc_usersubgroup_ids = res.data.map(s => s.id);
      } finally {
        this.loadingIdcSubgroups = false;
      }
    },

    onIdcToggle() {
      if (this.form.include_idc) {
        this.fetchIdcSubgroups();
      } else {
        this.idc_subgroups = [];
        this.form.idc_usersubgroup_ids = [];
      }
    },

    onSemesterChange() {
      this.form.usersubgroup_ids = this.filteredSubgroups.map(s => s.id);
      this.form.course_type_ids = [];
      this.idc_subgroups = [];
      this.form.idc_usersubgroup_ids = [];
      if (this.form.include_idc) this.fetchIdcSubgroups();
    },

    toggleGroup(group, checked) {
      const ids = group.courses.map(c => c.id);
      if (checked) {
        this.selectedCourseIds = [...new Set([...this.selectedCourseIds, ...ids])];
      } else {
        this.selectedCourseIds = this.selectedCourseIds.filter(id => !ids.includes(id));
      }
    },

    async initiateGrouping() {
      this.validated = true;

      if (this.form.grouping_level === 'PROGRAM') {
        if (!this.form.name.trim() || !this.form.program_ids.length || !this.form.scheme) return;
      } else {
        if (!this.form.name.trim() || !this.form.grouping_level) return;
      }

      this.previewing = true;
      try {
        this.previewCourses = await this.fetchFilteredCourses();
        this.selectedCourseIds = this.previewCourses.map(c => c.id);
        this.step = 'preview';
      } catch (e) {
        this.$toast.error("Failed to load courses for preview.", { position: "top", duration: 3000 });
      } finally {
        this.previewing = false;
      }
    },

    async onConfirmWarningAccepted() {
      this.showConfirmWarning = false;
      await this.confirmGrouping();
    },

    async confirmGrouping() {
      this.submitting = true;

      const courseIds = this.selectedCourseIds;
      const payload = {
        name:           this.form.name.trim(),
        grouping_level: this.form.grouping_level,
        course_ids:     courseIds,
      };

      if (this.form.grouping_level === 'PROGRAM') {
        payload.schemes = this.form.scheme;
        const allSubgroupIds = [...this.form.usersubgroup_ids, ...this.form.idc_usersubgroup_ids];
      if (allSubgroupIds.length) payload.usersubgroup_ids = allSubgroupIds;
      }

      const courseTypeIds = this.form.course_type_ids || [];
      if (courseTypeIds.length) payload.course_type_ids = courseTypeIds;

      let succeeded = false;
      try {
        await this.$axios.post(
          `${this.$store.getters.getAPIKey.mainAPI}/course-groups/generate`,
          payload
        );
        succeeded = true;
      } catch (error) {
        if (!error.response) {
          // No HTTP response (CORS / network race) but the request reached the server
          succeeded = true;
        } else {
          const msg = error.response.data?.message || "Failed to generate course group.";
          this.$toast.error(msg, { position: "top", duration: 4000 });
        }
      } finally {
        this.submitting = false;
      }

      if (succeeded) {
        this.$toast.success("Course group generated successfully.", { position: "top", duration: 3000 });
        this.$emit("success");
      }
    },

    resetForm() {
      this.validated           = false;
      this.previewing          = false;
      this.submitting          = false;
      this.showConfirmWarning  = false;
      this.step              = 'form';
      this.previewCourses    = [];
      this.selectedCourseIds = [];
      this.programSubgroups = [];
      this.idc_subgroups = [];
      this.form = {
        name: "",
        grouping_level: null,
        program_ids: [],
        scheme: null,
        semester: null,
        usersubgroup_ids: [],
        idc_usersubgroup_ids: [],
        include_idc: false,
        course_type_ids: [],
      };
    },
  },
};
</script>

<style scoped>
.multi-select-box {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px 12px;
  background: #fff;
}

.preview-table-wrap {
  max-height: 400px;
  overflow-y: auto;
}

.preview-table-wrap td {
  vertical-align: middle;
}

</style>

<style>
.group-shaded { background-color: #f8f9fa; }

.modal .multiselect__content-wrapper {
  z-index: 9999 !important;
  position: relative;
}
</style>
